import { useEffect, useRef, useState } from 'react';
import type { FacingMode, PermissionState } from '../types';

/**
 * Owns camera acquisition/permission state: requesting getUserMedia, tracking
 * every stream ever handed out (so all of them can be torn down together),
 * and cycling through available video input devices.
 */
export const useCameraStream = (
    videoRef: React.RefObject<HTMLVideoElement | null>,
    initialFacing: FacingMode = 'user',
    onPermissionChange?: (permission: PermissionState) => void,
) => {
    const [camera, setCamera] = useState<MediaStream | null>(null);
    const [permission, setPermission] = useState<PermissionState>({ status: false, error: null });
    const [facing, setFacing] = useState<FacingMode>(initialFacing);
    const [isLoading, setLoading] = useState(true);
    const [streamStack, setStack] = useState<MediaStream[]>([]);
    const [canFlipCamera, setCanFlip] = useState(false);
    const [availCameras, setAvailCameras] = useState<MediaDeviceInfo[]>([]);
    const [currentCamIdx, setCurrentIdx] = useState(0);

    // Mirror of streamStack for the unmount cleanup effect below, whose closure
    // is only created once (empty dependency array) and would otherwise only
    // ever see the initial empty array.
    const streamStackRef = useRef<MediaStream[]>([]);
    useEffect(() => {
        streamStackRef.current = streamStack;
    }, [streamStack]);

    useEffect(() => {
        const init = async () => {
            // askPermission never throws (it has its own try/catch), so this
            // always resolves and setLoading(false) always eventually runs —
            // otherwise a rejected/no-camera/insecure-context environment would
            // leave the loading overlay stuck forever.
            await askPermission(facing);

            // Enumerate only after permission has been requested: browsers
            // return anonymized (empty) deviceIds until a getUserMedia call has
            // been made on the origin, and Force Flip Camera depends on real ids.
            try {
                await checkAvailableCameras();
            } catch (error) {
                console.error(error);
            }
        }

        init();

        // Intentionally run once on mount; askPermission is manually re-invoked
        // by toggleFacing/cycleCamera/enableCamera when facing or device changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            streamStackRef.current.forEach(stream => stream.getTracks().forEach(track => track.stop()));
        }
    }, []);

    const updatePermission = (next: PermissionState) => {
        setPermission(next);
        onPermissionChange?.(next);
    }

    const checkAvailableCameras = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((device) => device.kind === 'videoinput');

        if (videoInputs.length > 1) {
            setCanFlip(true);
        }
        setAvailCameras(videoInputs);
    }

    const handleStreamAcquired = (stream: MediaStream) => {
        if (!videoRef.current) {
            return;
        }
        videoRef.current.muted = true; // it could be set in in-line style, yet neat to write in here
        videoRef.current.volume = 0;
        videoRef.current.srcObject = stream; // set video to play current MediaStream

        setCamera(stream);
        setStack(prevStack => [...prevStack, stream]);
        updatePermission({ status: true, error: null });
        setLoading(false);
    }

    const askPermission = async (facingMode: FacingMode, camId: string | null = null) => {
        const constraints: MediaStreamConstraints = camId
            ? { audio: true, video: { deviceId: { exact: camId } } }
            : { audio: true, video: { facingMode } }; // exact causes edge not working

        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            handleStreamAcquired(stream);
        } catch (error) {
            console.error(error);
            updatePermission({ status: false, error });
            setLoading(false);
        }
    }

    const stopMediaStream = () => {
        setStack(prevStack => {
            prevStack.forEach(stream => stream.getTracks().forEach(track => track.stop()));
            return [];
        });
        setCamera(null);
        // Otherwise the preview keeps showing a frozen frame from the now-dead
        // stream, e.g. if the follow-up askPermission (flip/cycle) fails.
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }

    const cycleCamera = async () => {
        setLoading(true);
        updatePermission({ status: false, error: null });
        stopMediaStream();

        if (availCameras.length === currentCamIdx) {
            await askPermission('user', availCameras[0].deviceId);
            setCurrentIdx(0);
            return;
        }
        await askPermission('user', availCameras[currentCamIdx].deviceId);
        setCurrentIdx(currentCamIdx + 1);
    }

    const toggleFacing = async () => {
        setLoading(true);
        updatePermission({ status: false, error: null });
        stopMediaStream();

        const nextFacing: FacingMode = facing === 'user' ? 'environment' : 'user';
        await askPermission(nextFacing);
        setFacing(nextFacing);
    }

    const destroyCamera = () => {
        if (!camera) {
            return;
        }
        stopMediaStream();
    }

    const enableCamera = async () => {
        setLoading(true);
        updatePermission({ status: false, error: null });
        await askPermission(facing);
    }

    return {
        camera,
        permission,
        facing,
        isLoading,
        canFlipCamera,
        toggleFacing,
        cycleCamera,
        destroyCamera,
        enableCamera,
    };
}
